import { createEffect, createImage, runEffect, clearCache, ReturnTypes, type EFFECTS } from '@picsart/editor-lib';
import 'services/editorLibFetchAI';
import PQueue from 'p-queue';

import pick from 'lodash/pick';
import type { IWfxEffect } from 'pages/Batch/sections/Sidebar/WfxTool/constants';
import type { IProcessResult } from './toolchain';
import { ConcurrentOperation, getConcurrencyLevel } from '..';

export interface EditorLibProcessAttrs {
  editorLibCategoryId: EFFECTS;
  effect: IWfxEffect;
}

const editorLibQueue = new PQueue({ concurrency: getConcurrencyLevel(ConcurrentOperation.API) });

export function cancelEditorLibQueue() {
  editorLibQueue.clear();
}

export function getEditorLibProcess({ editorLibCategoryId, effect }: EditorLibProcessAttrs) {
  return async (image: HTMLImageElement, isCancel: () => boolean): Promise<IProcessResult> => {
    const result = await editorLibQueue.add(
      async () => {
        if (isCancel()) throw new Error('cancelled');

        const effectInstance = createEffect(editorLibCategoryId);
        const imageData = createImage(image.src);

        const parameters = effectInstance.getParams();
        const effectParams = Array.isArray(parameters) ? parameters.map((item: { name: string }) => item.name) : [];
        const effectData = {
          ...pick(effect, effectParams),
          returnType: ReturnTypes.CANVAS,
        };

        try {
          const sourceCanvas = (await runEffect(imageData, effectInstance, effectData)) as HTMLCanvasElement;
          if (!sourceCanvas) {
            throw new Error('Failed to process image with editor-lib effect');
          }

          const blob = await new Promise<Blob>(res => {
            sourceCanvas.toBlob(b => res(b!), 'image/webp');
          });
          return { blob, result: URL.createObjectURL(blob) };
        } finally {
          clearCache(imageData._id);
          effectInstance.delete();
        }
      },
      { throwOnTimeout: true },
    );

    return result as IProcessResult;
  };
}
